import React, { useState, useCallback, useEffect } from 'react';
import './App.css';
import Header from './components/Header';
import PostLoginHeader from './components/PostLoginHeader';
import InteractiveCarousel from './components/InteractiveCarousel';
import VideoCategories from './components/GameCategories';
import VideosSection from './components/VideosSection';
import FavoritesSection from './components/FavoritesSection';
import LoginModal from './components/LoginModal';
import OTPModal from './components/OTPModal';
import RewardsPage from './components/RewardsPage';
import ProfilePage from './components/ProfilePage';
import SubscriptionPage from './components/SubscriptionPage';
import NewsPage from './components/NewsPage';
import UnsubscribePage from './components/UnsubscribePage';
import SubscriptionManagementPage from './components/SubscriptionManagementPage';
import VideosPage from './components/VideosPage';
import FavoritesPage from './components/FavoritesPage';
import ExploreVideosPage from './components/ExploreVideosPage';
import FAQPage from './components/FAQPage';
import AboutPage from './components/AboutPage';
import Notification from './components/Notification';
import ThemeToggle from './components/ThemeToggle';
import FloatingActionButton from './components/FloatingActionButton';
import Footer from './components/Footer';
import VideoPlayerModal from './components/VideoPlayerModal';
import { TranslationProvider } from './contexts/TranslationContext';
import { sendOtp, verifyOtp } from './services/otpService';
import {
  clearAllUserSession,
  getPendingAuth,
  getSubscription,
  PendingAuth,
  savePendingAuth,
  SubscriptionRecord,
} from './services/subscriptionStorage';
import { VideoContent } from './types/video';

type Page = 'home' | 'rewards' | 'profile' | 'subscription' | 'news' | 'unsubscribe' | 'subscription-management' | 'videos' | 'favorites' | 'explore' | 'faq' | 'about';

function AppContent() {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionRecord | null>(() => getSubscription());
  const [pendingAuth, setPendingAuth] = useState<PendingAuth | null>(() => getPendingAuth());
  const [authMsisdn, setAuthMsisdn] = useState('');
  const [sessionKey, setSessionKey] = useState(0);
  const [currentPage, setCurrentPage] = useState<Page>('home');
  const [activeVideo, setActiveVideo] = useState<VideoContent | null>(null);
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  const [currentTheme, setCurrentTheme] = useState<'light' | 'dark'>('dark');

  const isSubscribed = subscription !== null;
  const msisdn = subscription?.msisdn ?? pendingAuth?.msisdn ?? '';
  const phoneNumber = subscription?.phoneNumber ?? pendingAuth?.phoneNumber ?? '';
  const showPostLoginHeader = isSubscribed || pendingAuth !== null;

  useEffect(() => {
    const activeSubscription = getSubscription();
    setSubscription(activeSubscription);

    if (activeSubscription) {
      return;
    }

    const auth = getPendingAuth();
    setPendingAuth(auth);
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      const activeSubscription = getSubscription();
      setSubscription((current) => {
        if (current?.expiresAt !== activeSubscription?.expiresAt) {
          return activeSubscription;
        }
        return current;
      });

      if (!activeSubscription) {
        setPendingAuth(getPendingAuth());
      }
    }, 60000);

    return () => window.clearInterval(interval);
  }, []);

  const showNotification = useCallback((message: string, type: 'success' | 'error' | 'info') => {
    setNotification({ message, type });
  }, []);

  const handleVideoClick = useCallback((video: VideoContent) => {
    if (!isSubscribed) {
      setShowLoginModal(true);
      return;
    }

    setActiveVideo(video);
  }, [isSubscribed]);

  const handleLoginSubmit = useCallback(async (msisdnValue: string): Promise<boolean> => {
    const result = await sendOtp(msisdnValue);

    if (result.success) {
      setAuthMsisdn(msisdnValue);
      setShowLoginModal(false);
      setShowOTPModal(true);
      showNotification(`OTP sent successfully to +${msisdnValue}`, 'success');
      return true;
    }

    showNotification('There is an error please try again', 'error');
    return false;
  }, [showNotification]);

  const handleOTPVerify = useCallback(async (otp: string): Promise<boolean> => {
    const verifyMsisdn = authMsisdn || pendingAuth?.msisdn || subscription?.msisdn || '';
    const result = await verifyOtp(verifyMsisdn, otp);

    if (result.success) {
      const auth = savePendingAuth(verifyMsisdn, `+${verifyMsisdn}`);
      setPendingAuth(auth);
      setAuthMsisdn(verifyMsisdn);
      setShowOTPModal(false);
      setCurrentPage('subscription');
      showNotification('OTP verified successfully', 'success');
      return true;
    }

    showNotification(result.errorMessage || 'There is an error please try again', 'error');
    return false;
  }, [authMsisdn, pendingAuth, subscription, showNotification]);

  const handleSubscribeSuccess = useCallback(() => {
    const activeSubscription = getSubscription();
    setSubscription(activeSubscription);
    setPendingAuth(null);
    setCurrentPage('home');
  }, []);

  const handleBackToLogin = useCallback(() => {
    setShowOTPModal(false);
    setShowLoginModal(true);
  }, []);

  const handleCloseModals = useCallback(() => {
    setShowLoginModal(false);
    setShowOTPModal(false);
  }, []);

  const handleLogout = useCallback(() => {
    clearAllUserSession();
    setSubscription(null);
    setPendingAuth(null);
    setAuthMsisdn('');
    setShowLoginModal(false);
    setShowOTPModal(false);
    setActiveVideo(null);
    setNotification(null);
    setCurrentPage('home');
    setSessionKey((current) => current + 1);
  }, []);

  const handleNavigate = useCallback((page: string) => {
    if (page === 'login') {
      setShowLoginModal(true);
      return;
    }

    if (page === 'subscription') {
      if (isSubscribed || pendingAuth) {
        setCurrentPage('subscription');
      } else {
        setShowLoginModal(true);
      }
      return;
    }

    setCurrentPage(page as Page);
  }, [isSubscribed, pendingAuth]);

  const handleCloseNotification = useCallback(() => {
    setNotification(null);
  }, []);

  const handleThemeChange = useCallback((theme: 'light' | 'dark') => {
    setCurrentTheme(theme);
  }, []);

  const handleQuickAction = useCallback(() => {
    showNotification('Quick action activated! ⚡', 'info');
  }, [showNotification]);

  const renderPage = () => {
    switch (currentPage) {
      case 'rewards':
        return <RewardsPage />;
      case 'profile':
        return <ProfilePage />;
      case 'subscription':
        if (!pendingAuth && !isSubscribed) {
          return (
            <>
              <InteractiveCarousel key={`carousel-${sessionKey}`} onGameClick={handleVideoClick} />
              <FavoritesSection key={`favorites-${sessionKey}`} onVideoClick={handleVideoClick} onNavigate={handleNavigate} />
              <VideoCategories key={`categories-${sessionKey}`} onVideoClick={handleVideoClick} onNavigate={handleNavigate} />
              <VideosSection key={`videos-section-${sessionKey}`} onVideoClick={handleVideoClick} />
            </>
          );
        }
        return (
          <SubscriptionPage
            msisdn={msisdn}
            phoneNumber={phoneNumber}
            onSubscribeSuccess={handleSubscribeSuccess}
            onNotify={showNotification}
          />
        );
      case 'news':
        return <NewsPage />;
      case 'unsubscribe':
        return <UnsubscribePage onNavigate={handleNavigate} onLogout={handleLogout} />;
      case 'subscription-management':
        return <SubscriptionManagementPage onNavigate={handleNavigate} />;
      case 'videos':
        return <VideosPage onVideoClick={handleVideoClick} />;
      case 'favorites':
        return <FavoritesPage key={`favorites-page-${sessionKey}`} onVideoClick={handleVideoClick} />;
      case 'explore':
        return <ExploreVideosPage />;
      case 'faq':
        return <FAQPage />;
      case 'about':
        return <AboutPage />;
      default:
        return (
          <>
            <InteractiveCarousel key={`carousel-${sessionKey}`} onGameClick={handleVideoClick} />
            <FavoritesSection key={`favorites-${sessionKey}`} onVideoClick={handleVideoClick} onNavigate={handleNavigate} />
            <VideoCategories key={`categories-${sessionKey}`} onVideoClick={handleVideoClick} onNavigate={handleNavigate} />
            <VideosSection key={`videos-section-${sessionKey}`} onVideoClick={handleVideoClick} />
          </>
        );
    }
  };

  return (
    <div className="App" data-theme={currentTheme}>
      {showPostLoginHeader ? (
        <PostLoginHeader
          onLogout={handleLogout}
          onNavigate={handleNavigate}
          currentPage={currentPage}
          isSubscribed={isSubscribed}
        />
      ) : (
        <Header
          onNavigate={handleNavigate}
          currentPage={currentPage}
        />
      )}

      {renderPage()}

      <Footer />

      {showLoginModal && (
        <LoginModal
          onSubmit={handleLoginSubmit}
          onClose={handleCloseModals}
        />
      )}

      {showOTPModal && (
        <OTPModal
          phoneNumber={phoneNumber || (authMsisdn ? `+${authMsisdn}` : '')}
          onVerify={handleOTPVerify}
          onBack={handleBackToLogin}
          onClose={handleCloseModals}
        />
      )}

      {activeVideo && (
        <VideoPlayerModal
          video={activeVideo}
          onClose={() => setActiveVideo(null)}
        />
      )}

      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={handleCloseNotification}
        />
      )}

      <ThemeToggle onThemeChange={handleThemeChange} />
      <FloatingActionButton onQuickAction={handleQuickAction} />
    </div>
  );
}

function App() {
  return (
    <TranslationProvider>
      <AppContent />
    </TranslationProvider>
  );
}

export default App;
